import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import "../styles/AppStyles.css"; // Import your CSS styles
import { getDisplayNameFromUid } from "../utils/getDisplayNameFromUid";
import { formatDate } from "../utils/dateFunctions.js"; // Import your date formatting function

const UserProfile = () => {
  const { userId } = useParams();
  const [userDetails, setUserDetails] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lifetimeEarnings, setLifetimeEarnings] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [referredNames, setReferredNames] = useState({});
  const [totalBenefits, setTotalBenefits] = useState(0);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!userId) return;

        // Fetch user details
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          setError("User profile not found");
          setLoading(false);
          return;
        }

        const userData = userDoc.data();
        setUserDetails(userData); // Fetch transactions
        const transactionsRef = collection(db, "transactions");
        const q = query(transactionsRef, where("userId", "==", userData.bdaId));
        const querySnapshot = await getDocs(q);
        const transactionsList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort transactions by updatedAt in descending order
        const sortedTransactions = transactionsList.sort((a, b) => {
          const dateA =
            a.updatedAt?.toDate() || a.createdAt?.toDate() || new Date(0);
          const dateB =
            b.updatedAt?.toDate() || b.createdAt?.toDate() || new Date(0);
          return dateB - dateA;
        });

        setTransactions(sortedTransactions);
        // Fetch referred user display names if needed
        const referredIds = sortedTransactions
          .map((t) => t.referredUserId)
          .filter((id) => !!id);
        const namesMap = {};
        for (const refId of referredIds) {
          try {
            const dp = await getDisplayNameFromUid(refId);
            if (dp) {
              namesMap[refId] = dp;
            }
          } catch {
            namesMap[refId] = "";
          }
        }
        setReferredNames(namesMap);

        // Calculate totals
        const totalEarnings = transactionsList.reduce((sum, transaction) => {
          return sum + (transaction.amount || 0);
        }, 0);

        const paidAmount = transactionsList.reduce((sum, transaction) => {
          return transaction.paymentDate
            ? sum + (transaction.paidAmount || 0)
            : sum;
        }, 0);

        const benefitsRef = collection(db, "benefits");
        const b = query(benefitsRef, where("bdaId", "==", userData.bdaId));
        const benefitsQuerySnapshot = await getDocs(b);
        const benefitsList = benefitsQuerySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const totalBenefits = benefitsList.reduce((sum, benefit) => {
          return sum + (benefit.oneTimeIncome || 0);
        }, 0);
        setTotalBenefits(totalBenefits);

        const remainingAmount = userData.planAmount * 4 - paidAmount;
        setRemainingAmount(remainingAmount);

        setLifetimeEarnings(totalEarnings);
        setPaidAmount(paidAmount);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="profile-container">
      <div className="profile-header-row">
        <div>
          <h1 className="page-title">Member Profile</h1>
          <span className="page-subtitle">
            As of {new Date().toLocaleDateString()}
          </span>
        </div>

        <Link to="/admin/members" className="back-button">
          ← Back
        </Link>
      </div>

      <div className="user-info-card">
        <div className="user-info-header">
          <h3>Account Information</h3>
        </div>

        <div className="user-info-grid">
          <div>
            <label>Name</label>
            <span>{userDetails?.displayName || "Not set"}</span>
          </div>

          <div>
            <label>Plan</label>
            <span>{userDetails.investmentPlanName}</span>
          </div>

          <div>
            <label>Email</label>
            <span>{userDetails?.email || "Not set"}</span>
          </div>

          <div>
            <label>Phone</label>
            <span>{userDetails?.phone || "Not set"}</span>
          </div>

          <div>
            <label>BDA ID</label>
            <span>{userDetails?.bdaId || "Not set"}</span>
          </div>
        </div>
      </div>

      <div className="investment-highlights">
        <div className="highlight-card investment-plan">
          <div className="highlight-header">
            <h3>Invested Amount</h3>
          </div>
          <div className="highlight-content">
            <span className="highlight-value">
              ₹{userDetails?.planAmount?.toLocaleString("en-IN") || "0"}
            </span>
          </div>
        </div>
        <div className="highlight-card lifetime-earnings">
          <div className="highlight-header">
            <h3>Total Earnings</h3>
          </div>
          <div className="highlight-content">
            <span className="highlight-value">
              ₹{lifetimeEarnings.toLocaleString("en-IN")}
            </span>
          </div>
        </div>
        <div className="highlight-card lifetime-earnings">
          <div className="highlight-header">
            <h3>Paid Amount</h3>
          </div>
          <div className="highlight-content">
            <span className="highlight-value">
              ₹{paidAmount.toLocaleString("en-IN")}
            </span>
          </div>
        </div>
        <div className="highlight-card lifetime-earnings">
          <div className="highlight-header">
            <h3>Remaining Elgible Earnings</h3>
          </div>
          <div className="highlight-content">
            <span className="highlight-value">
              ₹{remainingAmount.toLocaleString("en-IN")}
            </span>
          </div>
        </div>
        <div className="highlight-card lifetime-earnings">
          <div className="highlight-header">
            <h3>Other Benefits</h3>
          </div>
          <div className="highlight-content">
            <span className="highlight-value">
              ₹{totalBenefits.toLocaleString("en-IN")}
            </span>
          </div>
        </div>
      </div>

      <div className="transactions-section">
        <h2>Transaction History</h2>
        {transactions.length > 0 ? (
          <div className="transactions-list">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Transaction Date</th>
                  <th>Transaction Type</th>
                  <th>Amount</th>
                  <th>Paid Amount</th>
                  <th>Remaining Amount</th>
                  <th>Payment Date</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>
                      {formatDate(transaction.createdAt) || "Invalid Date"}
                    </td>
                    <td>
                      {transaction.type === "assetPurchase"
                        ? "Asset Purchase"
                        : transaction.type}
                    </td>
                    <td>₹{transaction.amount?.toLocaleString("en-IN")}</td>
                    <td>₹{transaction.paidAmount?.toLocaleString("en-IN")}</td>

                    <td>
                      ₹{transaction.remainingAmount?.toLocaleString("en-IN")}
                    </td>
                    <td>{transaction.paymentDate || "-"}</td>
                    <td>
                      {transaction.referredUserId &&
                      referredNames[transaction.referredUserId]
                        ? ` - ${referredNames[transaction.referredUserId]}`
                        : ""}
                      {transaction.remarks ? ` - ${transaction.remarks}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="no-transactions">No transactions found</p>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
